import { useCallback, useEffect, useState } from 'react'
import { Box, useDisclosure } from '@chakra-ui/react'
import FileExplorer from '@/components/FileExplorer'
import CreateFolderModal from '@/components/Modals/CreateFolderModal'
import UploadModal from '@/components/Modals/UploadModal'
import QuickAccessCards from '@/components/Dashboard/QuickAccessCards'
import { useNavigation } from '@/hooks/useNavigation'
import { useHashNavigation } from '@/hooks/useHashNavigation'
import { useAppDispatch, useAppSelector } from '@/store'
import { selectPendingFolderNavigation } from '@/store/selectors'
import { folderNavigationHandled } from '@/store/slices/uploadsSlice'
import { startFileUploads, startFolderUpload } from '@/store/thunks/uploadThunks'
import { Folder } from '@/types'

export default function DashboardContent() {
  const dispatch = useAppDispatch()

  // Get navigation state and actions from context
  const { selectedFolder, refreshFolderTree, selectFolder } = useNavigation()

  // Enable hash-based navigation
  useHashNavigation()

  // Get folder tree from Redux to search for created folders
  const folderTree = useAppSelector((state) => state.folders.tree)
  const pendingFolderNavigation = useAppSelector(selectPendingFolderNavigation)

  // Modal state management
  const {
    isOpen: isCreateFolderOpen,
    onOpen: onCreateFolderOpen,
    onClose: onCreateFolderClose,
  } = useDisclosure()

  const { isOpen: isUploadOpen, onOpen: onUploadOpen, onClose: onUploadClose } = useDisclosure()

  // Which UploadModal tab to land on — 0 files, 1 folder. The root header offers both as
  // separate actions, and they share the one modal.
  const [uploadTab, setUploadTab] = useState<0 | 1>(0)

  const handleUploadFiles = useCallback(() => {
    setUploadTab(0)
    onUploadOpen()
  }, [onUploadOpen])

  const handleUploadFolder = useCallback(() => {
    setUploadTab(1)
    onUploadOpen()
  }, [onUploadOpen])

  // Helper function to find a folder by name in the tree
  const findFolderByName = (
    folders: Folder[],
    name: string,
    parentId?: number | null
  ): Folder | null => {
    for (const folder of folders) {
      // Check if this folder matches the name and parent
      if (folder.name === name && folder.parent_id === parentId) {
        return folder
      }

      // Recursively search in subfolders
      const children = folder.subfolders || folder.children || []
      if (children.length > 0) {
        const found = findFolderByName(children, name, parentId)
        if (found) return found
      }
    }
    return null
  }

  /**
   * Folder uploads run in a thunk, but jumping to the folder they created needs
   * the navigation context — so the thunk records the request and it is applied
   * here.
   */
  useEffect(() => {
    if (!pendingFolderNavigation) {
      return
    }

    const { rootFolderId, foldersCreated, rootFolderName } = pendingFolderNavigation
    let cancelled = false

    const navigate = async () => {
      await refreshFolderTree()
      if (cancelled) return

      if (rootFolderId) {
        const found = findFolderByName(folderTree, rootFolderName || '', selectedFolder?.id || null)
        if (found) {
          selectFolder(found)
        }
      } else if (foldersCreated > 0 && rootFolderName) {
        const found = findFolderByName(folderTree, rootFolderName, selectedFolder?.id || null)
        if (found) {
          selectFolder(found)
        }
      }

      dispatch(folderNavigationHandled())
    }

    void navigate()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingFolderNavigation])

  const handleFilesDropped = (files: File[]) => {
    void dispatch(
      startFileUploads({
        files,
        folderId: selectedFolder?.id ?? null,
        folderName: selectedFolder?.name ?? null,
      })
    )
  }

  const handleFolderDropped = (files: FileList) => {
    void dispatch(
      startFolderUpload({
        files,
        parentFolderId: selectedFolder?.id ?? null,
      })
    )
  }

  return (
    <Box display="flex" flexDirection="column" minH="100%">
      <FileExplorer
        onUpload={handleUploadFiles}
        onUploadFolder={handleUploadFolder}
        onCreateFolder={onCreateFolderOpen}
        onFilesDropped={handleFilesDropped}
        onFolderDropped={handleFolderDropped}
      />

      {!selectedFolder && <QuickAccessCards />}

      {/* Modals */}
      <CreateFolderModal
        isOpen={isCreateFolderOpen}
        onClose={onCreateFolderClose}
        parentFolder={selectedFolder}
        onFolderCreated={() => {
          refreshFolderTree()
          onCreateFolderClose()
        }}
      />

      <UploadModal
        isOpen={isUploadOpen}
        onClose={onUploadClose}
        targetFolder={selectedFolder}
        initialTabIndex={uploadTab}
      />
    </Box>
  )
}
